import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarCheck, PenLine, Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHora, hoyISO } from "@/lib/format";
import { TIPO_FICHAJE, fichajeEsReal } from "@/lib/fichajeUi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import CorreccionFichajeDialog from "@/components/CorreccionFichajeDialog";
import SolicitudPicadoManualDialog from "@/components/SolicitudPicadoManualDialog";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const ESTADO_CELDA = {
  ok: "border-emerald-300 bg-emerald-100 text-emerald-950",
  pendiente: "border-amber-300 bg-amber-100 text-amber-950",
  en_curso: "border-sky-300 bg-sky-100 text-sky-950",
  no_laborable: "border-zinc-200 bg-zinc-100 text-zinc-400",
  futuro: "border-zinc-100 bg-white text-zinc-300",
};

const ESTADO_LABEL = {
  ok: "Correcto",
  pendiente: "Revisar",
  en_curso: "En curso",
  no_laborable: "Sin jornada",
  futuro: "Futuro",
};

function primerDiaSemanaLunes(year, month) {
  // month 1-12 → weekday of day 1 (0=lun … 6=dom)
  const js = new Date(year, month - 1, 1).getDay();
  return js === 0 ? 6 : js - 1;
}

export default function CalendarioPicados() {
  const hoy = hoyISO();
  const [year, setYear] = useState(() => Number(hoy.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(hoy.slice(5, 7)));
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [diaSel, setDiaSel] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [correccion, setCorreccion] = useState(null);
  const [manualRegistro, setManualRegistro] = useState(null);
  const [confirmandoId, setConfirmandoId] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data: res } = await api.get("/trabajador/calendario-picados", {
        params: { year, month },
      });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [year, month]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const porFecha = useMemo(() => {
    const m = new Map();
    (data?.dias || []).forEach((d) => m.set(d.fecha, d));
    return m;
  }, [data]);

  const celdas = useMemo(() => {
    const offset = primerDiaSemanaLunes(year, month);
    const nDias = new Date(year, month, 0).getDate();
    const out = [];
    for (let i = 0; i < offset; i++) out.push(null);
    for (let d = 1; d <= nDias; d++) {
      const fecha = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      out.push(porFecha.get(fecha) || { fecha, estado: "futuro", motivos: [] });
    }
    return out;
  }, [year, month, porFecha]);

  const mesAnterior = () => {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else setMonth((m) => m - 1);
  };
  const mesSiguiente = () => {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else setMonth((m) => m + 1);
  };

  const abrirDia = async (celda) => {
    if (!celda?.fecha) return;
    if (celda.estado === "futuro") {
      toast.info("Ese día aún no se puede revisar");
      return;
    }
    setDiaSel(celda);
    setDetalle(null);
    setCargandoDetalle(true);
    try {
      const { data: ed } = await api.get("/fichajes/estado", { params: { dia: celda.fecha } });
      setDetalle(ed);
    } catch (e) {
      toast.error(formatApiError(e));
      setDiaSel(null);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const refrescarDetalle = async () => {
    if (!diaSel?.fecha) return;
    const { data: ed } = await api.get("/fichajes/estado", { params: { dia: diaSel.fecha } });
    setDetalle(ed);
    await cargar();
  };

  const confirmarSolicitud = async (solicitudId, aceptada) => {
    setConfirmandoId(solicitudId);
    try {
      await api.put(`/solicitudes/${solicitudId}/confirmar`, { aceptada, comentario: "" });
      toast.success(aceptada ? "Picado confirmado" : "Propuesta rechazada");
      await refrescarDetalle();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmandoId(null);
    }
  };

  const itemsDia = useMemo(() => {
    if (!detalle) return [];
    const items = [];
    for (const f of detalle.fichajes_hoy || []) {
      items.push({ ...f, es_faltante: false });
    }
    for (const p of detalle.picados_pendientes || []) {
      items.push({
        es_faltante: true,
        id: `faltante-${detalle.dia}-${p.tramo || p.franja}-${p.tipo_fichaje}`,
        tipo: p.tipo_fichaje,
        timestamp: p.timestamp_sugerido,
        fecha: detalle.dia,
        franja: p.franja,
        tramo: p.tramo,
        detalle: p.detalle,
        es_modificacion: p.es_modificacion,
        fichaje_id: p.fichaje_id,
      });
    }
    items.sort((a, b) => String(a.timestamp || "").localeCompare(String(b.timestamp || "")));
    return items;
  }, [detalle]);

  const solicitudesConfirmar = useMemo(() => {
    return (detalle?.solicitudes_fichaje_pendientes || []).filter(
      (s) => s.estado === "pendiente_confirmacion"
    );
  }, [detalle]);

  return (
    <div className="space-y-5" data-testid="calendario-picados-page">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
          <CalendarCheck className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-display text-xl font-semibold text-zinc-900">Calendario</h1>
          <p className="text-sm text-zinc-500">
            Verde = picados correctos · Ámbar = hay algo que corregir o confirmar
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-zinc-200 bg-white px-3 py-2 shadow-sm">
        <Button type="button" variant="ghost" size="icon" onClick={mesAnterior} data-testid="cal-picados-prev">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <p className="text-sm font-semibold text-zinc-900">
          {MESES[month - 1]} {year}
        </p>
        <Button type="button" variant="ghost" size="icon" onClick={mesSiguiente} data-testid="cal-picados-next">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {data?.resumen && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800">
            {data.resumen.ok || 0} correctos
          </Badge>
          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
            {data.resumen.pendiente || 0} a revisar
          </Badge>
          {(data.resumen.en_curso || 0) > 0 && (
            <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-800">
              {data.resumen.en_curso} en curso
            </Badge>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <div className="grid grid-cols-7 border-b border-zinc-100 bg-zinc-50 text-center text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
          {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
            <div key={d} className="py-2">{d}</div>
          ))}
        </div>
        {cargando ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-emerald-600" />
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1 p-2">
            {celdas.map((celda, idx) => {
              if (!celda) return <div key={`e-${idx}`} className="aspect-square" />;
              const diaNum = Number(celda.fecha.slice(8, 10));
              const esHoy = celda.fecha === hoy;
              const clickable = celda.estado !== "futuro";
              return (
                <button
                  key={celda.fecha}
                  type="button"
                  disabled={!clickable}
                  onClick={() => abrirDia(celda)}
                  data-testid={`cal-picados-dia-${celda.fecha}`}
                  className={`aspect-square rounded-xl border p-1 text-left transition ${
                    ESTADO_CELDA[celda.estado] || ESTADO_CELDA.futuro
                  } ${esHoy ? "ring-2 ring-indigo-400 ring-offset-1" : ""} ${
                    clickable ? "hover:brightness-95 active:scale-[0.98]" : "cursor-default"
                  }`}
                >
                  <span className="text-xs font-semibold tabular-nums">{diaNum}</span>
                  {celda.estado === "pendiente" && (
                    <span className="mt-0.5 block truncate text-[9px] font-medium opacity-80">
                      Revisar
                    </span>
                  )}
                  {celda.estado === "ok" && (
                    <span className="mt-0.5 block text-[9px] font-medium opacity-70">OK</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 text-[11px] text-zinc-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-emerald-200 border border-emerald-300" /> Correcto
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-amber-200 border border-amber-300" /> A revisar
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-sky-200 border border-sky-300" /> En curso
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-zinc-100 border border-zinc-200" /> Sin jornada
        </span>
      </div>

      <Sheet open={!!diaSel} onOpenChange={(v) => !v && setDiaSel(null)}>
        <SheetContent
          side="bottom"
          className="mx-auto w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-t-2xl"
          data-testid="cal-picados-dia-sheet"
        >
          <SheetHeader className="text-left">
            <SheetTitle>
              {diaSel ? fmtFecha(`${diaSel.fecha}T12:00:00`) : "Día"}
            </SheetTitle>
            <SheetDescription>
              {diaSel && (
                <Badge
                  variant="outline"
                  className={
                    diaSel.estado === "ok"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : diaSel.estado === "pendiente"
                        ? "border-amber-200 bg-amber-50 text-amber-800"
                        : ""
                  }
                >
                  {ESTADO_LABEL[diaSel.estado] || diaSel.estado}
                </Badge>
              )}
            </SheetDescription>
          </SheetHeader>

          {cargandoDetalle ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-zinc-200 border-t-emerald-600" />
            </div>
          ) : (
            <div className="mt-4 space-y-4 pb-6">
              {diaSel?.motivos?.length > 0 && (
                <ul className="space-y-1 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {diaSel.motivos.map((m) => (
                    <li key={m}>· {m}</li>
                  ))}
                </ul>
              )}

              {solicitudesConfirmar.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                    Confirmar propuestas
                  </p>
                  {solicitudesConfirmar.map((s) => (
                    <div
                      key={s.id}
                      className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3"
                    >
                      <p className="text-sm font-medium text-indigo-950">
                        {(TIPO_FICHAJE[s.tipo_fichaje]?.label || s.tipo_fichaje || "Fichaje")}
                        {s.timestamp_aprobado || s.timestamp_solicitado
                          ? ` · ${fmtHora(s.timestamp_aprobado || s.timestamp_solicitado)}`
                          : ""}
                      </p>
                      <div className="mt-2 flex gap-2">
                        <Button
                          size="sm"
                          className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                          disabled={confirmandoId === s.id}
                          onClick={() => confirmarSolicitud(s.id, true)}
                        >
                          <Check className="h-3.5 w-3.5" /> Confirmar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 border-rose-200 text-rose-700"
                          disabled={confirmandoId === s.id}
                          onClick={() => confirmarSolicitud(s.id, false)}
                        >
                          <X className="h-3.5 w-3.5" /> Rechazar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Picados del día
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1"
                  onClick={() =>
                    setManualRegistro({
                      open: true,
                      tipo: "entrada",
                      fecha: `${diaSel.fecha}T09:00:00`,
                      dia: diaSel.fecha,
                    })
                  }
                >
                  <Plus className="h-3.5 w-3.5" /> Añadir picado
                </Button>
              </div>

              {itemsDia.length === 0 ? (
                <p className="rounded-xl border border-dashed border-zinc-200 px-4 py-6 text-center text-sm text-zinc-400">
                  No hay picados registrados este día.
                </p>
              ) : (
                <ul className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
                  {itemsDia.map((f) => {
                    if (f.es_faltante) {
                      const meta = TIPO_FICHAJE[f.tipo] || TIPO_FICHAJE.salida;
                      return (
                        <li
                          key={f.id}
                          className="flex items-center justify-between gap-2 border-b border-amber-100 bg-amber-50/50 px-3 py-3 last:border-0"
                        >
                          <div>
                            <Badge variant="outline" className="border-amber-300 bg-amber-100 text-amber-900">
                              Falta {meta.label}
                            </Badge>
                            <p className="mt-1 text-xs text-amber-800">
                              {f.detalle || (f.franja ? `Tramo ${f.franja}` : "")}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1 border-amber-300 text-amber-800"
                            onClick={() => {
                              if (f.es_modificacion && f.fichaje_id) {
                                setCorreccion({
                                  fichaje_id: f.fichaje_id,
                                  timestamp: f.timestamp,
                                  fecha: f.fecha,
                                });
                              } else {
                                setManualRegistro({
                                  open: true,
                                  tipo: f.tipo,
                                  fecha: f.timestamp || `${f.fecha}T09:00:00`,
                                  dia: f.fecha,
                                });
                              }
                            }}
                          >
                            <PenLine className="h-3.5 w-3.5" />
                            {f.es_modificacion ? "Corregir" : "Registrar"}
                          </Button>
                        </li>
                      );
                    }
                    const meta = TIPO_FICHAJE[f.tipo] || TIPO_FICHAJE.salida;
                    const puedeCorregir = fichajeEsReal(f) && !f.pendiente_aprobacion && !f.es_informativo;
                    return (
                      <li
                        key={f.id}
                        className="flex items-center justify-between gap-2 border-b border-zinc-100 px-3 py-3 last:border-0"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium tabular-nums text-zinc-900">
                              {fmtHora(f.timestamp)}
                            </span>
                            <Badge variant="outline" className={meta.className}>
                              {meta.label}
                            </Badge>
                            {f.es_autocierre && (
                              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                                Auto
                              </Badge>
                            )}
                          </div>
                        </div>
                        {puedeCorregir && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 gap-1 text-indigo-700"
                            onClick={() =>
                              setCorreccion({
                                fichaje_id: f.id,
                                timestamp: f.timestamp,
                                fecha: detalle.dia,
                              })
                            }
                          >
                            <PenLine className="h-3.5 w-3.5" /> Corregir
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <CorreccionFichajeDialog
        item={correccion}
        onClose={() => setCorreccion(null)}
        onSuccess={refrescarDetalle}
      />
      <SolicitudPicadoManualDialog
        open={!!manualRegistro?.open}
        onOpenChange={(v) => !v && setManualRegistro(null)}
        defaultTipo={manualRegistro?.tipo}
        defaultTimestamp={manualRegistro?.fecha}
        defaultDia={manualRegistro?.dia}
        onSuccess={refrescarDetalle}
        presentacion="sheet"
        testId="cal-picados-manual-dialog"
      />
    </div>
  );
}
